import { useEffect, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import notificationSound from "../../assets/sound.mp3";

/**
 * Listener global de lembretes de agenda (CalendarEvent).
 *
 * Funcionamento:
 *  - Pede permissão de Notification API no primeiro mount (se ainda não concedida).
 *  - Escuta o evento socket `company${companyId}-calendar-event-reminder`.
 *  - Quando dispara, faz 3 coisas em paralelo:
 *      1) Notificação nativa do SO (Web Notifications API) — funciona no PC e
 *         no PWA mobile com a tela aberta ou em segundo plano.
 *      2) Toca um som curto de alerta.
 *      3) Mostra um toast no app.
 *  - Não duplica notificações pra um mesmo evento dentro da mesma sessão.
 */
const CalendarReminderListener = () => {
  const { user, socket } = useContext(AuthContext);
  const seenIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  // Cria o player de áudio uma vez
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.7;
  }, []);

  // Pede permissão de notificação. Browsers modernos exigem que a chamada
  // venha de um gesto do usuário (Safari/Firefox bloqueiam automatic).
  // Estratégia: tenta logo no mount, mas se o estado ficar "default" depois
  // disso, anexa um listener de 1ª interação (pointerdown) que tenta de novo.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined" || !("Notification" in window)) {
      console.warn("[CalendarReminder] Notification API indisponível neste navegador.");
      return;
    }
    const tryRequest = async () => {
      try {
        const result = await Notification.requestPermission();
        if (result === "denied") {
          toast.warn(
            i18n.t("calendarReminder.permissionDenied")
              || "Notificações bloqueadas. Habilite no navegador pra receber lembretes."
          );
        }
        return result;
      } catch (e) {
        console.warn("[CalendarReminder] erro ao pedir permissão:", e);
        return "default";
      }
    };

    if (Notification.permission === "default") {
      // 1ª tentativa imediata (funciona em Chrome/Edge mesmo sem gesto)
      tryRequest().then((result) => {
        if (result === "default") {
          // 2ª tentativa: aguarda gesto do usuário (Firefox/Safari)
          const onGesture = async () => {
            const r = await tryRequest();
            if (r !== "default") {
              window.removeEventListener("pointerdown", onGesture);
              window.removeEventListener("keydown", onGesture);
            }
          };
          window.addEventListener("pointerdown", onGesture, { once: false });
          window.addEventListener("keydown", onGesture, { once: false });
        }
      });
    } else if (Notification.permission === "denied") {
      // Permissao negada e uma preferencia do navegador; nao precisa poluir o console a cada carga.
    }
  }, [user]);

  // Listener do socket
  useEffect(() => {
    if (!user?.companyId || !socket) return;

    const onReminder = (data) => {
      const ev = data?.event;
      if (!ev || !ev.id) return;

      // Dedupe por id+notifiedAt na sessão atual
      const dedupeKey = `${ev.id}:${ev.notifiedAt || ""}`;
      if (seenIdsRef.current.has(dedupeKey)) return;
      seenIdsRef.current.add(dedupeKey);

      // 1) Som
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => { /* alguns browsers bloqueiam autoplay sem interação */ });
      } catch (e) { /* ignore */ }

      // 2) Notificação nativa (PC/PWA mobile)
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const minLabel =
            ev.reminder >= 60
              ? `${Math.round(ev.reminder / 60)} h`
              : `${ev.reminder} min`;
          const title = ev.title || i18n.t("schedules.calendar.viewModes.schedule") || "Agenda";
          const body =
            (ev.description ? `${ev.description}\n` : "") +
            `${i18n.t("calendarReminder.startsIn") || "Começa em"} ${minLabel}` +
            (ev.startTime ? ` · ${ev.startTime}` : "") +
            (ev.location ? ` · ${ev.location}` : "");
          const notif = new Notification(title, {
            body,
            tag: `calendar-event-${ev.id}`,
            requireInteraction: false,
            silent: false,
          });
          notif.onclick = () => {
            try { window.focus(); } catch (e) { /* ignore */ }
            try { window.location.href = "/schedules"; } catch (e) { /* ignore */ }
            notif.close();
          };
        }
      } catch (e) { /* ignore */ }

      // 3) Toast in-app
      const minLabel =
        ev.reminder >= 60
          ? `${Math.round(ev.reminder / 60)}h`
          : `${ev.reminder}min`;
      toast.info(
        `🔔 ${ev.title} — ${i18n.t("calendarReminder.startsIn") || "começa em"} ${minLabel}`,
        { autoClose: 8000 }
      );
    };

    const eventName = `company${user.companyId}-calendar-event-reminder`;
    socket.on(eventName, onReminder);
    return () => socket.off(eventName, onReminder);
  }, [socket, user?.companyId]);

  return null;
};

export default CalendarReminderListener;
